import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  Grid,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Drawer,
  Snackbar,
  TablePagination,
  FormControlLabel,
  Checkbox,
  Tabs,
  Tab,
  Skeleton,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import CakeOutlinedIcon from '@mui/icons-material/CakeOutlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { tokens } from '../../theme';
import { clientApi, Client, ClientDetail, ClientStats, CreateClientInput, clientEmail } from '../../services/clientService';
import { useSalonContextStore } from '../../store/salonContextStore';
import {
  PageHeader, SectionCard, StatTile, EmptyState, LoadingState, StatusPill, BookingStatusPill, PersonAvatar, FieldGrid, PillTone,
} from '../../components/ui';
import { formatMoney, formatNumber, formatDate, formatTime, countLabel } from '../../utils/format';

const SALE_STATUS: Record<string, { label: string; tone: PillTone }> = {
  COMPLETED: { label: 'Zrealizowana', tone: 'success' },
  REFUNDED: { label: 'Zwrot', tone: 'warning' },
  VOIDED: { label: 'Anulowana', tone: 'danger' },
};

const PAYMENT_LABELS: Record<string, string> = {
  CASH: 'Gotówka',
  CARD: 'Karta',
  BLIK: 'BLIK',
  TRANSFER: 'Przelew',
  VOUCHER: 'Bon',
};

// Statystyki bywaja niepelne (np. brak sredniego przychodu) — wtedy myslnik zamiast „undefined”.
const statValue = (v: unknown): string => (v === null || v === undefined || v === '' || Number.isNaN(Number(v)) ? '—' : formatNumber(v));

const tabular = { fontVariantNumeric: 'tabular-nums' } as const;

/** Wiersz danych w szczegolach klienta: ikona, etykieta, wartosc. */
const InfoRow: React.FC<{ icon: React.ReactNode; label: string; value?: React.ReactNode; empty?: string }> = ({ icon, label, value, empty = 'Nie podano' }) => (
  <Stack direction="row" gap={1.5} alignItems="flex-start" sx={{ py: 1 }}>
    <Box sx={{ color: tokens.subtle, display: 'flex', pt: 0.25, '& .MuiSvgIcon-root': { fontSize: 20 } }}>{icon}</Box>
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography variant="caption" sx={{ color: tokens.muted, display: 'block' }}>{label}</Typography>
      {value ? (
        <Typography sx={{ fontWeight: 600, wordBreak: 'break-word' }}>{value}</Typography>
      ) : (
        <Typography sx={{ color: tokens.subtle }}>{empty}</Typography>
      )}
    </Box>
  </Stack>
);

export const SalonClientsPage: React.FC = () => {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  const [searchParams, setSearchParams] = useSearchParams();
  const clientParam = searchParams.get('client');
  const qParam = searchParams.get('q');

  const [salonId, setSalonId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState(() => qParam || '');
  const [searchDebounced, setSearchDebounced] = useState(() => qParam || '');
  const [clients, setClients] = useState<Client[]>([]);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState<ClientStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Add client dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  // `noEmail` / `noPhone` to nie dane, tylko potwierdzenie recepcji, ze brak
  // jest swiadomy. Do bazy idzie po prostu pusta wartosc — o to prosil klient:
  // zaznaczyc "nie podal" zamiast wpisywac cokolwiek.
  const EMPTY_CLIENT_FORM = {
    firstName: '', lastName: '', email: '', phone: '', noEmail: false, noPhone: false,
  };
  const [addForm, setAddForm] = useState(EMPTY_CLIENT_FORM);
  // Uzupelnianie danych kartoteki juz istniejacej — klient z ulicy, ktory
  // przy kolejnej wizycie jednak podaje kontakt.
  const [editContact, setEditContact] = useState<{ email: string; phone: string } | null>(null);
  const [editSaving, setEditSaving] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState('');

  // Client detail
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [clientDetail, setClientDetail] = useState<ClientDetail | null>(null);
  const [detailTab, setDetailTab] = useState(0);
  // Id klienta, ktorego szczegoly sa otwarte lub wlasnie sie laduja — chroni przed
  // ponownym otwarciem z adresu i przed spoznionymi odpowiedziami.
  const detailRequestRef = useRef<string | null>(null);
  const { activeSalonId, loadSalons } = useSalonContextStore();

  useEffect(() => {
    loadSalons();
  }, [loadSalons]);

  // Fraza z adresu (?q=) jako wstepne wyszukiwanie
  useEffect(() => {
    if (qParam !== null) setSearch(qParam);
  }, [qParam]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounced(search);
      setPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  // Apply active salon
  useEffect(() => {
    if (activeSalonId) {
      setSalonId(activeSalonId);
      setError('');
    } else {
      setSalonId('');
      setError('Nie wybrano salonu');
    }
  }, [activeSalonId]);

  // Load clients
  const loadClients = useCallback(async () => {
    if (!salonId) return;
    setLoading(true);
    try {
      const res = await clientApi.listBySalon(salonId, page + 1, 20, searchDebounced || undefined);
      setClients(res.data);
      setTotal(res.pagination.total);
      setError('');
    } catch {
      setError('Nie udało się załadować klientów');
    } finally {
      setLoading(false);
    }
  }, [salonId, page, searchDebounced]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  // Load stats
  useEffect(() => {
    if (!salonId) return;
    setStatsLoading(true);
    clientApi.getStats(salonId).then(setStats).catch(() => {}).finally(() => setStatsLoading(false));
  }, [salonId]);

  // Add client
  const handleAddClient = async () => {
    setAddLoading(true);
    setAddError('');
    try {
      const input: CreateClientInput = {
        salonId,
        firstName: addForm.firstName,
        lastName: addForm.lastName,
        ...(addForm.email.trim() ? { email: addForm.email.trim() } : {}),
        ...(addForm.phone.trim() ? { phone: addForm.phone.trim() } : {}),
      };
      await clientApi.create(input);
      setSnackbar({ open: true, message: 'Klient dodany pomyślnie', severity: 'success' });
      setAddDialogOpen(false);
      setAddForm(EMPTY_CLIENT_FORM);
      loadClients();
      if (salonId) clientApi.getStats(salonId).then(setStats).catch(() => {});
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Nie udało się dodać klienta';
      setAddError(msg);
    } finally {
      setAddLoading(false);
    }
  };

  const clearClientParam = useCallback(() => {
    if (!searchParams.has('client')) return;
    const next = new URLSearchParams(searchParams);
    next.delete('client');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // Open client detail
  const handleOpenDetail = useCallback(async (clientId: string) => {
    detailRequestRef.current = clientId;
    setDetailOpen(true);
    setDetailLoading(true);
    setClientDetail(null);
    setDetailTab(0);
    setEditContact(null);
    try {
      const detail = await clientApi.getDetail(clientId, salonId);
      if (detailRequestRef.current !== clientId) return;
      setClientDetail(detail);
    } catch {
      if (detailRequestRef.current !== clientId) return;
      detailRequestRef.current = null;
      setSnackbar({ open: true, message: 'Nie udało się załadować szczegółów klienta', severity: 'error' });
      setDetailOpen(false);
      clearClientParam();
    } finally {
      if (detailRequestRef.current === clientId) setDetailLoading(false);
    }
  }, [salonId, clearClientParam]);

  const closeDetail = () => {
    detailRequestRef.current = null;
    setDetailOpen(false);
    clearClientParam();
  };

  // Wejscie z globalnej wyszukiwarki: /salon/clients?client=<id>
  useEffect(() => {
    if (!clientParam || !salonId) return;
    if (detailRequestRef.current === clientParam) return;
    handleOpenDetail(clientParam);
  }, [clientParam, salonId, handleOpenDetail]);

  const saveContact = async () => {
    if (!salonId || !clientDetail || !editContact) return;
    setEditSaving(true);
    try {
      await clientApi.updateBySalon(clientDetail.id, salonId, {
        email: editContact.email.trim(),
        phone: editContact.phone.trim(),
      });
      setSnackbar({ open: true, message: 'Dane zaktualizowane', severity: 'success' });
      setEditContact(null);
      const fresh = await clientApi.getDetail(clientDetail.id, salonId);
      setClientDetail(fresh);
      loadClients();
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Nie udało się zapisać danych';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setEditSaving(false);
    }
  };

  const closeAddDialog = () => { setAddDialogOpen(false); setAddError(''); };

  if (!salonId && !error) return <LoadingState variant="page" />;
  if (error && !salonId) {
    return (
      <Box>
        <PageHeader title="Klienci" />
        <Alert severity="warning">Nie wybrano salonu. Wybierz salon z listy na górnym pasku, aby zobaczyć jego klientów.</Alert>
      </Box>
    );
  }

  const description = loading && clients.length === 0
    ? 'Kartoteka klientów salonu: kontakt, historia wizyt i zakupów.'
    : searchDebounced
      ? `Znaleziono: ${countLabel(total, 'klient', 'klientów', 'klientów')}`
      : `${countLabel(total, 'klient', 'klientów', 'klientów')} w bazie`;

  const openRow = (id: string) => handleOpenDetail(id);
  const onRowKey = (e: React.KeyboardEvent, id: string) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openRow(id); }
  };

  const consentPills = (c: Client) => (
    c.marketingConsent || c.smsConsent ? (
      <Stack direction="row" gap={0.5} flexWrap="wrap">
        {c.marketingConsent && <StatusPill label="Marketing" tone="success" size="small" noDot />}
        {c.smsConsent && <StatusPill label="SMS" tone="success" size="small" noDot />}
      </Stack>
    ) : (
      <Typography variant="body2" sx={{ color: tokens.subtle }}>—</Typography>
    )
  );

  const pagination = total > 20 || page > 0 ? (
    <TablePagination
      component="div"
      count={total}
      page={page}
      onPageChange={(_, p) => setPage(p)}
      rowsPerPage={20}
      rowsPerPageOptions={[20]}
      labelDisplayedRows={({ from, to, count }) => `${from}–${to} z ${count}`}
      getItemAriaLabel={(type) => (type === 'next' ? 'Następna strona' : type === 'previous' ? 'Poprzednia strona' : type === 'first' ? 'Pierwsza strona' : 'Ostatnia strona')}
    />
  ) : null;

  return (
    <Box>
      <PageHeader
        title="Klienci"
        description={description}
        actions={(
          <Button variant="contained" startIcon={<PersonAddAlt1OutlinedIcon />} onClick={() => setAddDialogOpen(true)}>
            Dodaj klienta
          </Button>
        )}
      />

      {/* Statystyki */}
      {statsLoading && !stats ? (
        <Box sx={{ mb: 2.5 }}><LoadingState variant="tiles" count={4} /></Box>
      ) : stats && (
        <Box sx={{ display: 'grid', gap: 2, mb: 2.5, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' } }}>
          <StatTile label="Wszyscy klienci" value={statValue(stats.totalClients)} hint="w kartotece salonu" />
          <StatTile label="Powracający" value={statValue(stats.returningClients)} hint="z zakończoną wizytą lub zakupem" />
          <StatTile label="Nowi" value={statValue(stats.newClients)} hint="w ostatnich 30 dniach" />
          <StatTile
            label="Średni przychód na klienta"
            value={statValue(stats.avgSpent)}
            unit={statValue(stats.avgSpent) === '—' ? undefined : 'zł'}
            hint="wizyty i zakupy w kasie"
          />
        </Box>
      )}

      {/* Wyszukiwarka */}
      <TextField
        fullWidth
        placeholder="Szukaj po imieniu, nazwisku, telefonie lub e-mailu"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        inputProps={{ 'aria-label': 'Szukaj klienta' }}
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: tokens.muted }} /></InputAdornment>,
          endAdornment: search ? (
            <InputAdornment position="end">
              <IconButton size="small" aria-label="Wyczyść wyszukiwanie" onClick={() => setSearch('')}><CloseIcon fontSize="small" /></IconButton>
            </InputAdornment>
          ) : undefined,
        }}
        sx={{ mb: 2 }}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}. Odśwież stronę lub spróbuj ponownie za chwilę.</Alert>}

      {/* Lista klientow */}
      {loading && clients.length === 0 ? (
        <LoadingState variant="table" rows={6} />
      ) : clients.length === 0 ? (
        <SectionCard>
          {searchDebounced ? (
            <EmptyState
              icon={<SearchIcon />}
              title={`Brak klientów dla „${searchDebounced}”`}
              description="Sprawdź pisownię albo wyszukaj po numerze telefonu lub adresie e-mail."
              actions={<Button variant="outlined" onClick={() => setSearch('')}>Wyczyść wyszukiwanie</Button>}
            />
          ) : (
            <EmptyState
              icon={<PeopleAltOutlinedIcon />}
              title="Nie masz jeszcze klientów w bazie"
              description="Klienci pojawią się tu po pierwszej rezerwacji. Możesz też dodać ich ręcznie."
              actions={<Button variant="outlined" startIcon={<PersonAddAlt1OutlinedIcon />} onClick={() => setAddDialogOpen(true)}>Dodaj klienta</Button>}
            />
          )}
        </SectionCard>
      ) : isPhone ? (
        <Paper sx={{ overflow: 'hidden', opacity: loading ? 0.6 : 1, transition: 'opacity .15s ease' }}>
          <Stack divider={<Box sx={{ borderTop: `1px solid ${tokens.line}` }} />}>
            {clients.map((client) => {
              const email = clientEmail(client);
              return (
                <Stack
                  key={client.id}
                  direction="row"
                  alignItems="center"
                  gap={1.5}
                  role="button"
                  tabIndex={0}
                  onClick={() => openRow(client.id)}
                  onKeyDown={(e) => onRowKey(e, client.id)}
                  sx={{ px: 2, py: 1.5, cursor: 'pointer', '&:hover, &:focus-visible': { bgcolor: tokens.surface, outline: 'none' } }}
                >
                  <PersonAvatar firstName={client.firstName} lastName={client.lastName} seed={client.id} size={40} />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontWeight: 700 }} noWrap>{client.firstName} {client.lastName}</Typography>
                    <Typography variant="body2" sx={{ color: tokens.muted }} noWrap>
                      {client.phone || email || 'Brak danych kontaktowych'}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                    <Typography sx={{ fontWeight: 700, ...tabular }}>{formatMoney(client.totalSpent || 0)}</Typography>
                    <Typography variant="caption" sx={{ color: tokens.muted, ...tabular }}>
                      {countLabel(client._count?.bookings || 0, 'wizyta', 'wizyty', 'wizyt')}
                    </Typography>
                  </Box>
                </Stack>
              );
            })}
          </Stack>
          {pagination}
        </Paper>
      ) : (
        <Paper sx={{ overflow: 'hidden', opacity: loading ? 0.6 : 1, transition: 'opacity .15s ease' }}>
          <TableContainer>
            <Table sx={{ minWidth: 860 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Klient</TableCell>
                  <TableCell>Kontakt</TableCell>
                  <TableCell align="right">Wizyty</TableCell>
                  <TableCell align="right">Wydane</TableCell>
                  <TableCell>Ostatnia wizyta</TableCell>
                  <TableCell>Zgody</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {clients.map((client) => {
                  const email = clientEmail(client);
                  return (
                    <TableRow
                      key={client.id}
                      hover
                      tabIndex={0}
                      sx={{ cursor: 'pointer', '&:focus-visible': { outline: `2px solid ${tokens.plum}`, outlineOffset: -2 } }}
                      onClick={() => openRow(client.id)}
                      onKeyDown={(e) => onRowKey(e, client.id)}
                    >
                      <TableCell>
                        <Stack direction="row" alignItems="center" gap={1.5}>
                          <PersonAvatar firstName={client.firstName} lastName={client.lastName} seed={client.id} size={36} />
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: 'inherit' }}>
                              {client.firstName} {client.lastName}
                            </Typography>
                            {client.createdAt && (
                              <Typography variant="caption" sx={{ color: tokens.muted }}>
                                w bazie od {formatDate(client.createdAt)}
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack gap={0.25}>
                          {client.phone && (
                            <Stack direction="row" alignItems="center" gap={0.75}>
                              <PhoneOutlinedIcon sx={{ fontSize: 16, color: tokens.subtle }} />
                              <Typography variant="body2" sx={tabular}>{client.phone}</Typography>
                            </Stack>
                          )}
                          {email && (
                            <Stack direction="row" alignItems="center" gap={0.75} sx={{ minWidth: 0 }}>
                              <EmailOutlinedIcon sx={{ fontSize: 16, color: tokens.subtle }} />
                              <Typography variant="body2" noWrap sx={{ maxWidth: 240 }}>{email}</Typography>
                            </Stack>
                          )}
                          {!client.phone && !email && (
                            <Typography variant="body2" sx={{ color: tokens.subtle }}>Brak danych kontaktowych</Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell align="right" sx={tabular}>{formatNumber(client._count?.bookings || 0)}</TableCell>
                      <TableCell align="right" sx={{ ...tabular, fontWeight: 700 }}>{formatMoney(client.totalSpent || 0)}</TableCell>
                      <TableCell sx={tabular}>
                        {client.lastVisit ? formatDate(client.lastVisit) : <Box component="span" sx={{ color: tokens.subtle }}>—</Box>}
                      </TableCell>
                      <TableCell>{consentPills(client)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {pagination}
        </Paper>
      )}

      {/* Dodawanie klienta */}
      <Dialog open={addDialogOpen} onClose={closeAddDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Dodaj klienta</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: tokens.inkSoft, mb: 2.5 }}>
            Imię i nazwisko są wymagane. Jeśli klient nie chce podać e-maila lub telefonu, zaznacz to pod polem.
          </Typography>
          {addError && <Alert severity="error" sx={{ mb: 2 }}>{addError}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Imię"
                fullWidth
                required
                value={addForm.firstName}
                onChange={(e) => setAddForm({ ...addForm, firstName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Nazwisko"
                fullWidth
                required
                value={addForm.lastName}
                onChange={(e) => setAddForm({ ...addForm, lastName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Adres e-mail"
                type="email"
                fullWidth
                disabled={addForm.noEmail}
                value={addForm.email}
                onChange={(e) => setAddForm({ ...addForm, email: e.target.value })}
                placeholder="np. anna@przyklad.pl"
              />
              <FormControlLabel
                sx={{ mt: 0.5 }}
                control={
                  <Checkbox
                    size="small"
                    checked={addForm.noEmail}
                    onChange={(e) => setAddForm({ ...addForm, noEmail: e.target.checked, email: '' })}
                  />
                }
                label={<Typography variant="body2">Klient nie podał e-maila</Typography>}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Telefon"
                type="tel"
                fullWidth
                disabled={addForm.noPhone}
                value={addForm.phone}
                onChange={(e) => setAddForm({ ...addForm, phone: e.target.value })}
                placeholder="+48 600 000 000"
              />
              <FormControlLabel
                sx={{ mt: 0.5 }}
                control={
                  <Checkbox
                    size="small"
                    checked={addForm.noPhone}
                    onChange={(e) => setAddForm({ ...addForm, noPhone: e.target.checked, phone: '' })}
                  />
                }
                label={<Typography variant="body2">Klient nie podał telefonu</Typography>}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={closeAddDialog}>Anuluj</Button>
          <Button
            variant="contained"
            onClick={handleAddClient}
            startIcon={addLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
            disabled={
              addLoading ||
              !addForm.firstName.trim() ||
              !addForm.lastName.trim() ||
              !(addForm.email.trim() || addForm.noEmail) ||
              !(addForm.phone.trim() || addForm.noPhone)
            }
          >
            {addLoading ? 'Dodawanie…' : 'Dodaj klienta'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Szczegoly klienta */}
      <Drawer
        anchor="right"
        open={detailOpen}
        onClose={closeDetail}
        PaperProps={{ sx: { width: { xs: '100%', sm: 600 }, maxWidth: '100%', bgcolor: tokens.ground } }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }} role="dialog" aria-label="Szczegóły klienta">
          <Stack
            direction="row"
            alignItems="center"
            gap={1.5}
            sx={{ px: { xs: 2, sm: 3 }, py: 2, bgcolor: tokens.paper, borderBottom: `1px solid ${tokens.line}`, flexShrink: 0 }}
          >
            {detailLoading || !clientDetail ? (
              <>
                <Skeleton variant="circular" width={52} height={52} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton width="55%" height={28} />
                  <Skeleton width="35%" height={18} />
                </Box>
              </>
            ) : (
              <>
                <PersonAvatar firstName={clientDetail.firstName} lastName={clientDetail.lastName} seed={clientDetail.id} size={52} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography component="h2" variant="h3" sx={{ wordBreak: 'break-word' }}>
                    {clientDetail.firstName} {clientDetail.lastName}
                  </Typography>
                  <Typography variant="body2" sx={{ color: tokens.muted }}>
                    {clientDetail.createdAt ? `W bazie od ${formatDate(clientDetail.createdAt)}` : 'Kartoteka klienta'}
                  </Typography>
                </Box>
              </>
            )}
            <IconButton onClick={closeDetail} aria-label="Zamknij szczegóły" sx={{ alignSelf: 'flex-start' }}><CloseIcon /></IconButton>
          </Stack>

          <Box sx={{ flex: 1, overflowY: 'auto', px: { xs: 2, sm: 3 }, py: 2.5 }}>
            {detailLoading || !clientDetail ? (
              <Stack gap={2}>
                <LoadingState variant="block" height={120} />
                <LoadingState variant="table" rows={4} />
              </Stack>
            ) : (
              <Stack gap={2}>
                <SectionCard
                  title="Dane kontaktowe"
                  action={!editContact && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() =>
                        setEditContact({
                          email: clientEmail(clientDetail),
                          phone: clientDetail.phone || '',
                        })
                      }
                    >
                      Uzupełnij dane
                    </Button>
                  )}
                >
                  {editContact ? (
                    <Box>
                      <FieldGrid columns={2}>
                        <TextField
                          label="Adres e-mail"
                          type="email"
                          fullWidth
                          value={editContact.email}
                          onChange={(e) => setEditContact({ ...editContact, email: e.target.value })}
                          helperText={!clientEmail(clientDetail) ? 'Podanie adresu założy klientowi konto do logowania' : undefined}
                        />
                        <TextField
                          label="Telefon"
                          type="tel"
                          fullWidth
                          value={editContact.phone}
                          onChange={(e) => setEditContact({ ...editContact, phone: e.target.value })}
                        />
                      </FieldGrid>
                      <Stack direction="row" gap={1} sx={{ mt: 2 }} flexWrap="wrap">
                        <Button
                          variant="contained"
                          disabled={editSaving}
                          startIcon={editSaving ? <CircularProgress size={16} color="inherit" /> : undefined}
                          onClick={saveContact}
                        >
                          {editSaving ? 'Zapisywanie…' : 'Zapisz dane'}
                        </Button>
                        <Button variant="text" onClick={() => setEditContact(null)} disabled={editSaving}>Anuluj</Button>
                      </Stack>
                    </Box>
                  ) : (
                    <Box>
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' }, columnGap: 2 }}>
                        <InfoRow icon={<PhoneOutlinedIcon />} label="Telefon" value={clientDetail.phone} />
                        <InfoRow icon={<EmailOutlinedIcon />} label="E-mail" value={clientEmail(clientDetail)} />
                        <InfoRow icon={<CakeOutlinedIcon />} label="Data urodzenia" value={clientDetail.dateOfBirth ? formatDate(clientDetail.dateOfBirth) : undefined} />
                        {clientDetail.allergies && <InfoRow icon={<EventNoteOutlinedIcon />} label="Alergie" value={clientDetail.allergies} />}
                        {clientDetail.preferences && <InfoRow icon={<EventNoteOutlinedIcon />} label="Preferencje" value={clientDetail.preferences} />}
                      </Box>
                      <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 1.5 }}>
                        <StatusPill
                          label={clientDetail.marketingConsent ? 'Zgoda na marketing' : 'Brak zgody na marketing'}
                          tone={clientDetail.marketingConsent ? 'success' : 'neutral'}
                        />
                        <StatusPill
                          label={clientDetail.smsConsent ? 'Zgoda na SMS' : 'Brak zgody na SMS'}
                          tone={clientDetail.smsConsent ? 'success' : 'neutral'}
                        />
                      </Stack>
                    </Box>
                  )}
                </SectionCard>

                <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
                  <StatTile label="Odbyte wizyty" value={formatNumber(clientDetail.stats.totalVisits)} />
                  <StatTile label="Wydane na wizyty" value={formatNumber(clientDetail.stats.totalSpentBookings)} unit="zł" />
                  <StatTile label="Zakupy w kasie" value={formatNumber(clientDetail.stats.totalSales)} />
                  <StatTile label="Łącznie wydane" value={formatNumber(clientDetail.stats.totalSpent)} unit="zł" />
                </Box>

                <Paper sx={{ overflow: 'hidden' }}>
                  <Tabs
                    value={detailTab}
                    onChange={(_, v) => setDetailTab(v)}
                    variant="fullWidth"
                    sx={{ px: 1, borderBottom: `1px solid ${tokens.line}` }}
                  >
                    <Tab label={`Wizyty (${clientDetail.bookings.length})`} />
                    <Tab label={`Zakupy (${clientDetail.sales?.length || 0})`} />
                  </Tabs>

                  {detailTab === 0 && (
                    clientDetail.bookings.length === 0 ? (
                      <EmptyState
                        compact
                        icon={<EventNoteOutlinedIcon />}
                        title="Brak wizyt"
                        description="Wizyty tego klienta pojawią się tutaj po pierwszej rezerwacji."
                      />
                    ) : (
                      <Stack divider={<Box sx={{ borderTop: `1px solid ${tokens.line}` }} />}>
                        {clientDetail.bookings.map((b) => (
                          <Stack key={b.id} direction="row" gap={1.5} alignItems="flex-start" sx={{ px: { xs: 2, sm: 2.5 }, py: 1.5 }}>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography sx={{ fontWeight: 650 }}>{b.service.name}</Typography>
                              <Typography variant="body2" sx={{ color: tokens.muted, ...tabular }}>
                                {formatDate(b.date)}, {b.startTime} · {b.staff.firstName} {b.staff.lastName}
                              </Typography>
                            </Box>
                            <Stack alignItems="flex-end" gap={0.5} sx={{ flexShrink: 0 }}>
                              <Typography sx={{ fontWeight: 700, ...tabular }}>{formatMoney(b.totalPrice)}</Typography>
                              <BookingStatusPill status={b.status} size="small" />
                            </Stack>
                          </Stack>
                        ))}
                      </Stack>
                    )
                  )}

                  {detailTab === 1 && (
                    !clientDetail.sales || clientDetail.sales.length === 0 ? (
                      <EmptyState
                        compact
                        icon={<ReceiptLongOutlinedIcon />}
                        title="Brak zakupów"
                        description="Zakupy zarejestrowane w kasie pojawią się tutaj."
                      />
                    ) : (
                      <Stack divider={<Box sx={{ borderTop: `1px solid ${tokens.line}` }} />}>
                        {clientDetail.sales.map((s) => {
                          const st = SALE_STATUS[s.status] || { label: s.status, tone: 'neutral' as PillTone };
                          const itemsSummary = s.items.map((i) => (i.quantity > 1 ? `${i.name} ×${i.quantity}` : i.name)).join(', ');
                          return (
                            <Stack key={s.id} direction="row" gap={1.5} alignItems="flex-start" sx={{ px: { xs: 2, sm: 2.5 }, py: 1.5 }}>
                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography sx={{ fontWeight: 650, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                                  {itemsSummary || 'Sprzedaż'}
                                </Typography>
                                <Typography variant="body2" sx={{ color: tokens.muted, ...tabular }}>
                                  {formatDate(s.createdAt)}, {formatTime(s.createdAt)} · {s.staff.firstName} {s.staff.lastName} · {PAYMENT_LABELS[s.paymentMethod] || s.paymentMethod}
                                </Typography>
                              </Box>
                              <Stack alignItems="flex-end" gap={0.5} sx={{ flexShrink: 0 }}>
                                <Typography sx={{ fontWeight: 700, ...tabular }}>{formatMoney(s.totalAmount)}</Typography>
                                <StatusPill label={st.label} tone={st.tone} size="small" />
                              </Stack>
                            </Stack>
                          );
                        })}
                      </Stack>
                    )
                  )}
                </Paper>
              </Stack>
            )}
          </Box>
        </Box>
      </Drawer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert variant="filled" severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
